/**
 * The import queue (W2-09 publish-import; DESIGN.md §5.12; ARCHITECTURE.md §11): every drop and file pick goes
 * through it. Imports run one after the other in the io worker; several charts or folders give one project each and
 * one summary report at the end ("Library progress": the banner shows "Importing 2 of 5" with a Cancel button).
 *
 * Per import, by what the io worker found:
 *  - a chart: when it is the only chart of the run, the import report dialog asks "Open as new project" or "Add into
 *    the current project at a beat" (then the project is created in persist and opened, or merged; mergeFlow.ts);
 *    in a run of several, a new project is created directly (imports never overwrite a project);
 *  - a resource pack: the skin install dialog (validated like Phira; installs through the skin store);
 *  - a Phira batch export: the batch picker chooses its charts, which join the queue as imports of their own;
 *  - only media: the new chart wizard starts with the imported song and picture (in a run of several, the summary
 *    offers it);
 *  - an error: reported (toast for a single import, the summary for several).
 *
 * The queue is observable (`subscribe` / `snapshot`) for the banner and the summary, which render package-internal
 * dialogs (batch picker, summary) through the banner host; without a mounted host the picker falls back to the
 * system choice dialog.
 */

import type { AppServices } from '../../app/registry/types';
import { DIALOG } from '../../app/dialogs/types';
import type { ProjectId } from '../../core/model/types';
import type { MessageRef, Unsubscribe } from '../../core/util/types';
import type { ImportedAsset, ImportInput, ImportReport, ImportResult } from '../../formats/types';
import { m } from './import.messages';
import { missingCount, planJobs, readJob, transferables, type ImportJob, type Plan } from './importFlow';
import { mergeIntoProject, readMergeAlign } from './mergeFlow';

export interface JobInfo {
  readonly id: number;
  readonly name: string;
}

export type Outcome =
  | {
      readonly kind: 'project';
      readonly job: JobInfo;
      readonly projectId: ProjectId;
      readonly title: string;
      readonly report: ImportReport;
      readonly missing: number;
      readonly opened: boolean;
    }
  | {
      readonly kind: 'merged';
      readonly job: JobInfo;
      readonly report: ImportReport;
      readonly missing: number;
      readonly lines: number;
    }
  | { readonly kind: 'skin'; readonly job: JobInfo; readonly name: string; readonly installedId: string | null }
  | { readonly kind: 'batch'; readonly job: JobInfo; readonly picked: number; readonly total: number }
  | {
      readonly kind: 'media';
      readonly job: JobInfo;
      readonly assets: readonly ImportedAsset[];
      readonly started: boolean;
    }
  | { readonly kind: 'cancelled'; readonly job: JobInfo }
  | { readonly kind: 'failed'; readonly job: JobInfo; readonly error: MessageRef; readonly detail?: string };

export interface BatchPrompt {
  readonly job: JobInfo;
  readonly charts: ReadonlyArray<{ readonly name: string; readonly bytes: number }>;
}

export interface QueueSnapshot {
  readonly running: boolean;
  /** The import in progress (`waiting`: a dialog waits for the user). */
  readonly current: {
    readonly job: JobInfo;
    readonly index: number;
    readonly progress: number | null;
    readonly waiting: boolean;
  } | null;
  /** Imports in this run (grows when a batch export adds its charts or more files are dropped). */
  readonly total: number;
  /** Outcomes of the current run, or of the last one when idle. */
  readonly outcomes: readonly Outcome[];
  readonly prompt: BatchPrompt | null;
  readonly summaryOpen: boolean;
  /** The "Import finished" banner of a run of several (until dismissed or the next run starts). */
  readonly finishedBanner: boolean;
  /** Counts runs (a new run starts with an empty outcome list). */
  readonly run: number;
  /** The mounted banner host that draws the queue's dialogs (the first one mounted), or null. */
  readonly primaryHost: number | null;
}

const IDLE: QueueSnapshot = {
  running: false,
  current: null,
  total: 0,
  outcomes: [],
  prompt: null,
  summaryOpen: false,
  finishedBanner: false,
  run: 0,
  primaryHost: null,
};

function errorText(e: unknown): string {
  if (e instanceof Error && e.message !== '') return e.message;
  if (typeof e === 'object' && e !== null) {
    const msg = (e as { readonly message?: unknown }).message;
    if (typeof msg === 'string' && msg !== '') return msg;
  }
  return typeof e === 'string' ? e : '';
}

/** "The import stopped unexpectedly: <error>" (the error's own words, else "unknown error"). */
function unexpected(services: Pick<AppServices, 'i18n'>, e: unknown): MessageRef {
  const text = errorText(e);
  return { key: m.errorUnexpected, params: { error: text !== '' ? text : services.i18n.t(m.unknownError) } };
}

/** Projects created by a run (for the summary banner). */
export function createdCount(outcomes: readonly Outcome[]): number {
  return outcomes.filter((o) => o.kind === 'project').length;
}

/** Imports of a run that did not produce anything because of an error. */
export function failedCount(outcomes: readonly Outcome[]): number {
  return outcomes.filter((o) => o.kind === 'failed').length;
}

type ChartResult = Extract<ImportResult, { readonly kind: 'chart' }>;

type FailedOutcome = Extract<Outcome, { readonly kind: 'failed' }>;

/** Failures of single imports this close together are told in one toast. */
export const FAILURE_GROUP_MS = 900;

export class ImportQueue {
  private snap: QueueSnapshot = IDLE;
  private pending: ImportJob[] = [];
  /** Imports cancelled while one was running (listed after it). */
  private cancelled: ImportJob[] = [];
  /** Imports of the run that may be charts (a batch export counts as its chosen charts). */
  private chartJobs = 0;
  private idSeq = 0;
  private hosts: number[] = [];
  private hostSeq = 0;
  private promptResolve: ((picked: readonly number[] | null) => void) | null = null;
  private showProblemsNext = false;
  private loop: Promise<void> | null = null;
  private readonly listeners = new Set<() => void>();

  // ---- observation ----

  snapshot(): QueueSnapshot {
    return this.snap;
  }

  subscribe(cb: () => void): Unsubscribe {
    this.listeners.add(cb);
    return () => {
      this.listeners.delete(cb);
    };
  }

  /** A fresh banner host token (`attachHost` registers it while the host is mounted). */
  hostToken(): number {
    return ++this.hostSeq;
  }

  /**
   * A banner host is mounted. The first mounted host draws the batch picker and the summary (`primaryHost`); the
   * returned function detaches it.
   */
  attachHost(token: number): Unsubscribe {
    this.hosts.push(token);
    this.set({ primaryHost: this.hosts[0] ?? null });
    let done = false;
    return () => {
      if (done) return;
      done = true;
      this.hosts = this.hosts.filter((h) => h !== token);
      this.set({ primaryHost: this.hosts[0] ?? null });
    };
  }

  /** Resolves when the queue is idle (and a grouped failure toast has been shown). */
  async idle(): Promise<void> {
    if (this.loop !== null) await this.loop;
    await this.burst?.shown;
  }

  /**
   * Failures of single imports that come in a quick series (several bad files dropped one after another) are told
   * in ONE toast with "View report" (UX-088): a failure waits `FAILURE_GROUP_MS` for the next one.
   */
  private burst: {
    readonly failed: FailedOutcome[];
    timer: ReturnType<typeof setTimeout> | null;
    readonly shown: Promise<void>;
    readonly resolve: () => void;
  } | null = null;

  private noteFailure(services: AppServices, outcome: FailedOutcome): void {
    if (this.burst === null) {
      let resolve: () => void = () => undefined;
      const shown = new Promise<void>((r) => {
        resolve = r;
      });
      this.burst = { failed: [], timer: null, shown, resolve };
    }
    const b = this.burst;
    b.failed.push(outcome);
    if (b.timer !== null) clearTimeout(b.timer);
    b.timer = setTimeout(() => {
      b.timer = null;
      // Another import is running: its end decides (it may fail too).
      if (!this.snap.running) this.flushFailures(services);
    }, FAILURE_GROUP_MS);
  }

  private flushFailures(services: AppServices): void {
    const b = this.burst;
    if (b === null) return;
    this.burst = null;
    if (b.timer !== null) clearTimeout(b.timer);
    const action = { label: { key: m.viewReport }, command: 'file.importSummary' } as const;
    const first = b.failed[0];
    if (b.failed.length === 1 && first !== undefined) {
      services.ui.toast(
        { key: m.toastFailed, params: { name: first.job.name, error: services.i18n.tr(first.error) } },
        { kind: 'error', action },
      );
    } else if (b.failed.length > 1) {
      // The report lists every failure of the series.
      this.set({ outcomes: b.failed });
      services.ui.toast(
        {
          key: m.toastFailedMany,
          params: { count: b.failed.length, names: b.failed.map((o) => o.job.name).join(', ') },
        },
        { kind: 'error', action },
      );
    }
    b.resolve();
  }

  // ---- input ----

  /** Plans and queues the imports of a drop or a pick. */
  enqueue(services: AppServices, files: readonly File[]): Plan {
    const plan = planJobs(files, () => ++this.idSeq);
    if (plan.skipped > 0) services.ui.toast({ key: m.toastSkipped, params: { count: plan.skipped } }, { kind: 'info' });
    if (plan.jobs.length === 0) {
      services.ui.toast({ key: m.toastNothing }, { kind: 'warning' });
      return plan;
    }
    this.add(services, plan.jobs);
    return plan;
  }

  /** Queues prepared imports (tests, batch exports). */
  add(services: AppServices, jobs: readonly ImportJob[]): void {
    if (this.snap.running) {
      this.pending.push(...jobs);
      this.chartJobs += jobs.length;
      this.set({ total: this.snap.total + jobs.length });
      services.ui.toast({ key: m.toastQueued, params: { count: jobs.length } }, { kind: 'info' });
      return;
    }
    this.pending = [...jobs];
    this.chartJobs = jobs.length;
    this.set({
      running: true,
      current: null,
      total: jobs.length,
      outcomes: [],
      prompt: null,
      summaryOpen: false,
      finishedBanner: false,
      run: this.snap.run + 1,
      primaryHost: this.hosts[0] ?? null,
    });
    this.loop = this.runLoop(services).finally(() => {
      this.loop = null;
    });
  }

  /**
   * Cancels the imports not started yet (the current one finishes; they are listed as cancelled after it, in their
   * queue order).
   */
  cancel(services: AppServices): void {
    const dropped = this.pending;
    this.pending = [];
    if (this.promptResolve !== null) this.answerPrompt(null);
    if (dropped.length === 0) return;
    this.cancelled.push(...dropped);
    if (!this.snap.running) this.flushCancelled();
    services.ui.toast({ key: m.toastCancelled, params: { count: dropped.length } }, { kind: 'info' });
  }

  private flushCancelled(): void {
    if (this.cancelled.length === 0) return;
    const jobs = this.cancelled;
    this.cancelled = [];
    this.set({
      outcomes: [
        ...this.snap.outcomes,
        ...jobs.map((j): Outcome => ({ kind: 'cancelled', job: { id: j.id, name: j.name } })),
      ],
    });
  }

  answerPrompt(picked: readonly number[] | null): void {
    const resolve = this.promptResolve;
    this.promptResolve = null;
    this.set({ prompt: null });
    resolve?.(picked);
  }

  /** "Open and show in Problems" of the report dialog: the Problems panel opens after the project. */
  requestProblemsAfterOpen(): void {
    this.showProblemsNext = true;
  }

  openSummary(): void {
    if (this.snap.outcomes.length > 0) this.set({ summaryOpen: true });
  }

  closeSummary(): void {
    this.set({ summaryOpen: false });
  }

  dismissBanner(): void {
    this.set({ finishedBanner: false });
  }

  /** Forget everything (tests). */
  reset(): void {
    if (this.burst !== null) {
      if (this.burst.timer !== null) clearTimeout(this.burst.timer);
      this.burst.resolve();
      this.burst = null;
    }
    this.pending = [];
    this.cancelled = [];
    this.chartJobs = 0;
    this.promptResolve = null;
    this.showProblemsNext = false;
    this.set({ ...IDLE, primaryHost: this.hosts[0] ?? null });
  }

  // ---- processing ----

  private set(patch: Partial<QueueSnapshot>): void {
    this.snap = { ...this.snap, ...patch };
    for (const cb of [...this.listeners]) cb();
  }

  private setCurrent(patch: Partial<NonNullable<QueueSnapshot['current']>>): void {
    const cur = this.snap.current;
    if (cur !== null) this.set({ current: { ...cur, ...patch } });
  }

  private async runLoop(services: AppServices): Promise<void> {
    let index = 0;
    for (let job = this.pending.shift(); job !== undefined; job = this.pending.shift()) {
      index++;
      this.set({ current: { job: { id: job.id, name: job.name }, index, progress: null, waiting: false } });
      let outcome: Outcome;
      try {
        outcome = await this.process(services, job);
      } catch (e) {
        outcome = {
          kind: 'failed',
          job: { id: job.id, name: job.name },
          error: unexpected(services, e),
        };
      }
      this.set({ outcomes: [...this.snap.outcomes, outcome] });
      this.flushCancelled();
    }
    this.flushCancelled();
    const outcomes = this.snap.outcomes;
    const several = outcomes.length > 1;
    this.set({ running: false, current: null, prompt: null, summaryOpen: several, finishedBanner: several });
    if (several) {
      services.ui.toast(
        { key: m.bannerDone, params: { created: createdCount(outcomes), failed: failedCount(outcomes) } },
        {
          kind: failedCount(outcomes) > 0 ? 'warning' : 'success',
          action: { label: { key: m.viewReport }, command: 'file.importSummary' },
        },
      );
      return;
    }
    const only = outcomes[0];
    if (only?.kind === 'failed') {
      this.noteFailure(services, only);
      return;
    }
    // Something else came after a series of failures: tell those now.
    if (this.burst !== null && this.burst.timer === null) this.flushFailures(services);
  }

  private interactive(): boolean {
    return this.chartJobs === 1;
  }

  private async process(services: AppServices, job: ImportJob): Promise<Outcome> {
    const info: JobInfo = { id: job.id, name: job.name };
    let input: ImportInput;
    try {
      input = await readJob(job);
    } catch (e) {
      return { kind: 'failed', job: info, error: unexpected(services, e) };
    }
    const off = services.io.on('progress', (e) => {
      if (e.task === 'import') this.setCurrent({ progress: e.p });
    });
    let result: ImportResult;
    try {
      result = await services.io.call('importPackage', { input }, transferables(input));
    } catch (e) {
      return { kind: 'failed', job: info, error: unexpected(services, e) };
    } finally {
      off();
    }
    this.setCurrent({ progress: 1 });
    switch (result.kind) {
      case 'chart':
        return this.chart(services, info, result);
      case 'respack':
        return this.respack(services, info, result);
      case 'batch':
        return this.batch(services, info, result.charts);
      case 'mediaOnly':
        return this.media(services, info, result.assets);
      case 'error':
        return {
          kind: 'failed',
          job: info,
          error: result.error,
          ...(result.detail !== undefined ? { detail: result.detail } : {}),
        };
    }
  }

  private async waitFor<T>(p: () => Promise<T>): Promise<T> {
    this.setCurrent({ waiting: true });
    try {
      return await p();
    } finally {
      this.setCurrent({ waiting: false });
    }
  }

  private async createProject(services: AppServices, result: ChartResult): Promise<ProjectId> {
    return services.persist.createProject(
      result.doc,
      result.assets.map((a) => ({ id: a.id, bytes: a.bytes, mime: a.mime, role: a.role, meta: a.meta })),
    );
  }

  private afterOpen(services: AppServices): void {
    if (!this.showProblemsNext) return;
    this.showProblemsNext = false;
    services.ui.showPanel('problems');
  }

  private async chart(services: AppServices, job: JobInfo, result: ChartResult): Promise<Outcome> {
    const report = result.report;
    const missing = missingCount(result.doc);
    const title = result.doc.meta.name.trim() !== '' ? result.doc.meta.name : job.name;
    if (!this.interactive()) {
      try {
        const projectId = await this.createProject(services, result);
        return { kind: 'project', job, projectId, title, report, missing, opened: false };
      } catch (e) {
        return { kind: 'failed', job, error: { key: m.createFailed, params: { error: errorText(e) } } };
      }
    }
    this.showProblemsNext = false;
    const choice = await this.waitFor(() =>
      services.ui.openDialog(DIALOG.importReport, { result, canMerge: services.store !== null }),
    );
    if (choice === null) return { kind: 'cancelled', job };
    if (choice.action === 'merge') {
      const out = await mergeIntoProject(
        services,
        result,
        choice.beatOffset,
        choice.align ?? readMergeAlign(services.settings),
      );
      if (!out.ok) {
        services.ui.toast(out.reason, { kind: 'error' });
        return { kind: 'failed', job, error: out.reason };
      }
      services.ui.toast(
        { key: m.toastMerged, params: { lines: out.lines, name: report.fileName } },
        { kind: 'success' },
      );
      for (const note of out.notes.slice(0, 3)) services.ui.toast(note, { kind: 'info' });
      this.afterOpen(services);
      return { kind: 'merged', job, report, missing, lines: out.lines };
    }
    let projectId: ProjectId;
    try {
      projectId = await this.createProject(services, result);
    } catch (e) {
      const error: MessageRef = { key: m.createFailed, params: { error: errorText(e) } };
      return { kind: 'failed', job, error };
    }
    services.ui.toast({ key: m.toastCreated, params: { name: title } }, { kind: 'success' });
    try {
      await services.nav.openProject(projectId);
    } catch (e) {
      // The project exists (Library); opening it can be retried from there.
      services.ui.toast({ key: m.openFailed, params: { error: errorText(e) } }, { kind: 'error' });
      return { kind: 'project', job, projectId, title, report, missing, opened: false };
    }
    this.afterOpen(services);
    return { kind: 'project', job, projectId, title, report, missing, opened: true };
  }

  private async respack(
    services: AppServices,
    job: JobInfo,
    result: Extract<ImportResult, { readonly kind: 'respack' }>,
  ): Promise<Outcome> {
    const name = result.pack?.info.name ?? job.name;
    const out = await this.waitFor(() =>
      services.ui.openDialog(DIALOG.skinInstall, { pack: result.pack, validation: result.validation }),
    );
    return { kind: 'skin', job, name, installedId: out?.installedId ?? null };
  }

  private async batch(
    services: AppServices,
    job: JobInfo,
    charts: ReadonlyArray<{ readonly name: string; readonly bytes: Uint8Array }>,
  ): Promise<Outcome> {
    const picked = await this.waitFor(() => this.askBatch(services, job, charts));
    const chosen = (picked ?? []).filter((i) => i >= 0 && i < charts.length);
    const jobs: ImportJob[] = [];
    for (const i of chosen) {
      const c = charts[i];
      if (c === undefined) continue;
      jobs.push({
        id: ++this.idSeq,
        name: `${job.name} / ${c.name}`,
        source: { kind: 'bytes', name: c.name, bytes: c.bytes },
      });
    }
    // The batch export itself is not a chart; its chosen charts are.
    this.chartJobs += jobs.length - 1;
    this.pending.unshift(...jobs);
    this.set({ total: this.snap.total + jobs.length });
    return { kind: 'batch', job, picked: jobs.length, total: charts.length };
  }

  private askBatch(
    services: AppServices,
    job: JobInfo,
    charts: ReadonlyArray<{ readonly name: string; readonly bytes: Uint8Array }>,
  ): Promise<readonly number[] | null> {
    if (this.hosts.length === 0) {
      // No host to draw the picker: the system choice dialog picks one chart.
      return services.ui
        .choose({
          title: { key: m.batchTitle },
          body: { key: m.batchBody, params: { count: charts.length } },
          options: charts.map((c, i) => ({ value: String(i), label: { key: m.plainName, params: { name: c.name } } })),
        })
        .then((v) => (v === null ? null : [Number(v)]));
    }
    return new Promise((resolve) => {
      this.promptResolve = resolve;
      this.set({ prompt: { job, charts: charts.map((c) => ({ name: c.name, bytes: c.bytes.length })) } });
    });
  }

  private async media(services: AppServices, job: JobInfo, assets: readonly ImportedAsset[]): Promise<Outcome> {
    if (!(this.interactive() && this.snap.total === 1)) return { kind: 'media', job, assets, started: false };
    const started = await startWizard(services, assets, (p) => this.waitFor(p));
    return { kind: 'media', job, assets, started };
  }
}

/** Starts the new chart wizard with imported media (the first song and the first picture). */
export async function startWizard(
  services: AppServices,
  assets: readonly ImportedAsset[],
  wrap: <T>(p: () => Promise<T>) => Promise<T> = (p) => p(),
): Promise<boolean> {
  const music = assets.find((a) => a.role === 'music');
  const illustration = assets.find((a) => a.role === 'illustration');
  const result = await wrap(() =>
    services.ui.openDialog(DIALOG.wizard, {
      ...(music !== undefined ? { music } : {}),
      ...(illustration !== undefined ? { illustration } : {}),
    }),
  );
  return result !== null;
}

/** The app's import queue. */
export const importQueue = new ImportQueue();
